import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { settingsService } from '@/services/settings.service';
import { useAuth } from '@/hooks/useAuth';

interface ModuleAccess { enabled: string[] | null; loading: boolean; error: boolean; isPlatformOwner: boolean; has: (key: string) => boolean; refresh: () => void; }
const Context = createContext<ModuleAccess>({ enabled: [], loading: true, error: false, isPlatformOwner: false, has: () => false, refresh: () => undefined });
export const useModuleAccess = () => useContext(Context);

export function ModuleAccessProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState<string[] | null>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const version = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++version.current;
    if (user?.role === 'super_admin') { setEnabled(null); setIsPlatformOwner(true); setLoading(false); setError(false); return; }
    try {
      const result = await settingsService.enabledModules();
      if (request !== version.current) return;
      setEnabled(result.enabledModules); setIsPlatformOwner(result.isPlatformOwner === true); setError(false);
    } catch { if (request === version.current) { setEnabled([]); setIsPlatformOwner(false); setError(true); } }
    finally { if (request === version.current) setLoading(false); }
  }, [user?.role, user?.companyUid]);
  useEffect(() => {
    setEnabled([]); setLoading(true); setIsPlatformOwner(false); void refresh();
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => { version.current++; window.removeEventListener('focus', onFocus); };
  }, [refresh]);
  const has = useCallback((key: string) => enabled === null || enabled.includes(key), [enabled]);
  return <Context.Provider value={{ enabled, loading, error, isPlatformOwner, has, refresh }}>{children}</Context.Provider>;
}
